import {
  tablesDB,
  DATABASE_ID,
  REQUESTS_TABLE_ID,
  ORDERS_TABLE_ID,
  USERS_TABLE_ID,
  Query,
} from '../lib/appwrite';
import { pickCurrentRequest } from '../utils/deliveryRequestUtils';

/**
 * Get delivery requests with store, customer, and driver details for display.
 */
export async function getDeliveryRequestsWithDetails(queries = []) {
  const [requestsRes, ordersRes, usersRes] = await Promise.all([
    tablesDB.listRows(DATABASE_ID, REQUESTS_TABLE_ID, [
      Query.limit(5000),
      Query.orderDesc('createdAt'),
      ...queries,
    ]),
    tablesDB.listRows(DATABASE_ID, ORDERS_TABLE_ID, [Query.limit(5000)]),
    tablesDB.listRows(DATABASE_ID, USERS_TABLE_ID, [Query.limit(3000)]),
  ]);

  const requests = requestsRes.rows || [];
  const orders = ordersRes.rows || [];
  const users = usersRes.rows || [];

  const orderIdToCustomerId = {};
  orders.forEach((o) => {
    const key = o.orderId || o.$id;
    if (!orderIdToCustomerId[key] && o.customerId) {
      orderIdToCustomerId[key] = o.customerId;
    }
  });

  const userMap = {};
  users.forEach((u) => {
    userMap[u.$id] = {
      name: [u.fname, u.lname].filter(Boolean).join(' ') || u.email || u.$id,
      email: u.email || '',
      phone: u.phoneNumber || u.phone || '',
    };
  });

  return requests.map((r) => {
    const customerId = r.orderId ? orderIdToCustomerId[r.orderId] : null;
    const customer = customerId ? userMap[customerId] : null;
    return {
      ...r,
      store: {
        name: r.storeName || '—',
        contact: r.storeContact || '',
      },
      customer: customer
        ? {
            name: customer.name || '—',
            email: customer.email || r.receiverContact || '',
            phone: customer.phone || r.receiverContact || '',
          }
        : {
            name: '—',
            email: '',
            phone: r.receiverContact || '',
          },
      driver: {
        name: r.driverName || '—',
        contact: r.driverContact || '',
      },
    };
  });
}

/**
 * Get a delivery request by ID (for tracking map)
 */
export async function getDeliveryRequest(requestId) {
  const row = await tablesDB.getRow(DATABASE_ID, REQUESTS_TABLE_ID, requestId);
  return row;
}

/**
 * List delivery requests (for order-driver mapping)
 */
export async function listDeliveryRequests(queries = []) {
  const res = await tablesDB.listRows(DATABASE_ID, REQUESTS_TABLE_ID, [
    Query.limit(5000),
    Query.orderDesc('$createdAt'),
    ...queries,
  ]);
  return res.rows || [];
}

/**
 * Get delivery request by order ID (for order detail / tracking).
 *
 * An order can have several requests behind it — a cancelled booking left
 * behind by a rebooked delivery — so this takes the current one, not
 * whichever single row Appwrite happens to return first.
 */
export async function getDeliveryRequestByOrderId(orderId) {
  const res = await tablesDB.listRows(DATABASE_ID, REQUESTS_TABLE_ID, [
    Query.equal('orderId', orderId),
    Query.orderDesc('$createdAt'),
    Query.limit(25),
  ]);
  return pickCurrentRequest(res.rows || []);
}
